import GroupIcon from '@mui/icons-material/Group';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
} from '@mui/material';

import HeaderComp from '../../components/elements/HeadingComponent';
import useNavigateWithUUID from '../../hooks/useNavigateWithUUID';
import colors from '../../theme/colors';

const Administration = () => {
  const { navigateWithUuid } = useNavigateWithUUID();

  const administrationOptions = [
    {
      id: 'user-management',
      title: 'User Management',
      description: 'Manage user accounts, permissions, and access controls',
      icon: GroupIcon,
      onClick: () => navigateWithUuid('administration/user-management'),
    },
    // {
    //   id: 'payment-history',
    //   title: 'Payment & History',
    //   description:
    //     'Handle billing, subscriptions, and view transaction history',
    //   icon: PaymentIcon,
    //   onClick: () => navigateWithUuid('administration/payment-history'),
    // },
    // {
    //   id: 'topup-balance',
    //   title: 'Top Up Balance',
    //   description: 'Add funds to your account balance and manage payments',
    //   icon: AccountBalanceWalletIcon,
    //   onClick: () => navigateWithUuid('administration/topup-balance'),
    // },
  ];

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 6 }}>
        <HeaderComp mb={2}>Administration</HeaderComp>
      </Box>

      {/* Administration Options Grid */}
      <Grid
        container
        spacing={3}
        sx={{
          mb: 6,
        }}
      >
        {administrationOptions.map((option) => {
          const Icon = option.icon;
          return (
            <Grid item xs={12} md={3} key={option.id} sx={{ display: 'flex' }}>
              <Card
                sx={{
                  width: '100%',
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'rgba(0, 128, 128, 0.08)',
                  bgcolor: 'rgba(255, 255, 255, 0.6)',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  borderRadius: 1.5,
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: 'rgba(0, 128, 128, 0.2)',
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                  },
                }}
                onClick={option.onClick}
              >
                <CardContent
                  sx={{
                    p: 2.5,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  {/* Icon Container */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        background: 'rgba(66, 211, 146, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'rgba(66, 211, 146, 0.12)',
                          transform: 'scale(1.02)',
                        },
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: 26,
                          color: colors.gradientColor1,
                          transition: 'all 0.2s ease',
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Content */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      mb: 1.5,
                      color: 'text.primary',
                      fontSize: '0.95rem',
                    }}
                  >
                    {option.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      mb: 2.5,
                      lineHeight: 1.5,
                      fontSize: '0.8rem',
                      flex: 1,
                    }}
                  >
                    {option.description}
                  </Typography>

                  {/* Button */}
                  <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    sx={{
                      height: 36,
                      fontWeight: 500,
                      borderColor: 'rgba(18, 112, 220, 0.2)',
                      color: 'primary.main',
                      borderRadius: 1,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        borderColor: 'primary.main',
                        boxShadow: '0 2px 6px rgba(18, 112, 220, 0.2)',
                        transform: 'scale(1.02)',
                      },
                    }}
                  >
                    Manage
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Quick Tips Section */}
      <Card
        sx={{
          background: '#fbfcfc',
          border: '1px solid',
          borderColor: 'rgba(0, 128, 128, 0.06)',
          borderRadius: 1.5,
          mb: 4,
          boxShadow: 'none',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'rgba(0, 128, 128, 0.15)',
            bgcolor: 'rgba(255, 255, 255, 0.6)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
          },
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                bgcolor: 'primary.main',
                p: 0.75,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              <GroupIcon sx={{ fontSize: 18, color: 'white' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                  mb: 1,
                  color: 'text.primary',
                  fontSize: '0.95rem',
                }}
              >
                Administration Overview
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.5,
                  fontSize: '0.8rem',
                }}
              >
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  <Box component="li" sx={{ mb: 0.5 }}>
                    <strong style={{ color: 'text.primary' }}>
                      User Management
                    </strong>{' '}
                    allows you to control user access and permissions.
                  </Box>
                  <Box component="li" sx={{ mb: 0.5 }}>
                    <strong style={{ color: 'text.primary' }}>
                      Payment & History
                    </strong>{' '}
                    handles billing, subscriptions, and transaction history.
                  </Box>
                  <Box component="li">
                    <strong style={{ color: 'text.primary' }}>
                      Top Up Balance
                    </strong>{' '}
                    enables you to add funds and manage account balance.
                  </Box>
                </Box>
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Administration;
